import { useCallback } from 'react';
import copyToClipboard from 'copy-to-clipboard';

import { Modals } from 'Services/Modals';
import { ModalHeader } from 'App/Modals/templates/Default/components/ModalHeader';
import { SnackBar } from 'Services/SnackBar';
import { isSystemShareSupported } from 'Shared/Share/utils';
import { useBreakpoints } from 'Shared/ResponsiveBreakpoints';
import { Button } from 'UI/Forms/Button';
import { InputWithActionButton } from 'UI/Forms/Fields/InputWithActionButton';

import { BUTTON_SIZE } from '../constants';
import {
  trackCopyClick,
  trackDoneClick,
  trackGenerateLinkPopupDismissed,
  trackShareClick,
} from '../../biEvents';
import { ShareMoreText } from '../ShareMoreText';

type PropsT = {
  availableGiftTotal: number;
  giftLink: string;
  onMobileShare: (url: string, addSourceParam?: boolean) => void;
  ariaDialogLabelId: string;
};

export const ShareLinkModal: React.FunctionComponent<PropsT> = ({
  availableGiftTotal,
  giftLink,
  onMobileShare,
  ariaDialogLabelId,
}) => {
  const { isMdScreen } = useBreakpoints();

  const handleClick = useCallback(() => {
    if (isSystemShareSupported) {
      trackShareClick(availableGiftTotal);
      onMobileShare(giftLink, false);
    } else {
      trackCopyClick(availableGiftTotal);
      copyToClipboard(giftLink);
      SnackBar.push({ content: 'Gift Link Copied' });
    }
  }, [availableGiftTotal, giftLink, onMobileShare]);

  const handleDone = useCallback(() => {
    Modals.removeCurrentModal();
    trackDoneClick(availableGiftTotal);
  }, [availableGiftTotal]);

  const handleClose = useCallback(() => {
    trackGenerateLinkPopupDismissed('active', availableGiftTotal);
    Modals.removeCurrentModal();
  }, [availableGiftTotal]);

  return (
    <>
      <ModalHeader
        ariaDialogLabelId={ariaDialogLabelId}
        closeLabel="Close"
        title="Gift Article"
        variant="action"
        onClose={handleClose}
      />

      <div className="mb-24 flex grow flex-col justify-between pt-18 md:pt-16">
        <div>
          <InputWithActionButton
            wide
            buttonText={isSystemShareSupported ? 'Share' : 'Copy'}
            dataTestId="share-link-input"
            label="Share Link"
            rootClassName="mb-18 md:mb-32"
            size={isMdScreen ? 'medium' : 'large'}
            value={giftLink}
            onButtonClick={handleClick}
          />

          {availableGiftTotal === 0 ? (
            <>
              This is your final share link for the month. You can share{' '}
              <b>5 more</b> articles next month.
            </>
          ) : (
            <ShareMoreText availableGiftTotal={availableGiftTotal} />
          )}
        </div>

        <Button
          wide
          dataTestId="done-button"
          size={BUTTON_SIZE}
          variant="black"
          onClick={handleDone}
        >
          Done
        </Button>
      </div>
    </>
  );
};
